import { useState } from 'react';
import { Alert, Button, Dropdown, Form, Spinner } from 'react-bootstrap';
import { HiOutlineUserCircle } from 'react-icons/hi2';
import { Link, useLocation, useNavigate } from '@/compat/react-router-dom';
import { partnerPortalAuthService } from '@/api/services';
import { usePartnerPortalAuth } from '@/states';
import { getPartnerPortalPathForRole } from '@/utils/partnerRoutes';
import { toPathSegment } from '@/utils/imageUrl';

/**
 * Compact partner login panel for the site header (dropdown / popover style).
 */
const HeaderPartnerLoginDropdown = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { saveSession } = usePartnerPortalAuth();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [identity, setIdentity] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState({ identity: '', password: '' });

  const onSubmit = async (event) => {
    event.preventDefault();
    setError('');
    const trimmedEmail = identity.trim().toLowerCase();
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
    const nextFieldErrors = {
      identity: emailOk ? '' : 'Enter a valid email address',
      password: password ? '' : 'Password required',
    };
    setFieldErrors(nextFieldErrors);
    if (nextFieldErrors.identity || nextFieldErrors.password) return;

    setIsSubmitting(true);
    try {
      const response = await partnerPortalAuthService.login({
        email: trimmedEmail,
        password,
      });
      const payload = response?.data || {};
      if (!payload?.token || !payload?.user) {
        throw new Error('Invalid login response');
      }
      saveSession({ token: payload.token, user: payload.user });
      const fallbackPath = getPartnerPortalPathForRole(
        payload.user?.partner_slug,
        payload.user?.role
      );
      const safePartnerSlug = toPathSegment(payload.user?.partner_slug, '');
      const slugPrefix = safePartnerSlug ? `/partner/${encodeURIComponent(safePartnerSlug)}/` : '';
      const requestedPath =
        typeof location.state?.from === 'string' ? location.state.from : '';
      const redirectTo = requestedPath.startsWith(slugPrefix)
        ? requestedPath
        : fallbackPath;
      setOpen(false);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          'Sign-in failed. Check your details and try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dropdown show={open} onToggle={setOpen} align="end">
      <Dropdown.Toggle
        variant="link"
        id="header-login-toggle"
        className="site-header-gyg__action site-header-gyg__action--toggle text-decoration-none text-body d-flex flex-column align-items-center gap-1 p-1 border-0 shadow-none"
      >
        <HiOutlineUserCircle className="site-header-gyg__action-icon" />
        <span className="site-header-gyg__action-label">Log in</span>
      </Dropdown.Toggle>
      <Dropdown.Menu className="site-header-gyg__login-panel shadow border-0 p-3">
        <div className="small fw-semibold mb-3">Partner portal</div>
        {error ? (
          <Alert variant="danger" className="py-2 small mb-3">
            {error}
          </Alert>
        ) : null}
        <Form onSubmit={onSubmit} noValidate>
          <Form.Group className="mb-3">
            <Form.Control
              type="email"
              size="sm"
              placeholder="Email"
              value={identity}
              isInvalid={Boolean(fieldErrors.identity)}
              onChange={(e) => {
                setIdentity(e.target.value);
                if (fieldErrors.identity) {
                  setFieldErrors((prev) => ({ ...prev, identity: '' }));
                }
              }}
            />
            {fieldErrors.identity ? (
              <Form.Control.Feedback type="invalid">
                {fieldErrors.identity}
              </Form.Control.Feedback>
            ) : null}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Control
              type="password"
              size="sm"
              placeholder="Password"
              value={password}
              isInvalid={Boolean(fieldErrors.password)}
              onChange={(e) => {
                setPassword(e.target.value);
                if (fieldErrors.password) {
                  setFieldErrors((prev) => ({ ...prev, password: '' }));
                }
              }}
            />
            {fieldErrors.password ? (
              <Form.Control.Feedback type="invalid">
                {fieldErrors.password}
              </Form.Control.Feedback>
            ) : null}
          </Form.Group>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            className="w-100 mt-1"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Signing in…
              </>
            ) : (
              'Sign in'
            )}
          </Button>
        </Form>
        <div className="text-center mt-3 pt-1">
          <Link to="/partner/forgot-password" className="small" onClick={() => setOpen(false)}>
            Forgot password
          </Link>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default HeaderPartnerLoginDropdown;
